/*
 * Copyright (c) 2026. Bolmate B.V.
 *
 * The per-notification delivery picker: "stuur een notificatie via
 * [multi-select] naar [address]". One PortalSelect lists the e-mail channel
 * plus the account's push devices (replacing the old separate e-mail input +
 * push toggle + enable switch):
 *
 * - Every notification's configuration is INDEPENDENT: e-mail mirrors its
 *   EMAIL channel; the device rows mirror its WEB_PUSH channel PLUS its own
 *   targeting list (flex.push_device_ids — absent = all devices, legacy). A
 *   device row is CHECKED only when the notification carries WEB_PUSH and its
 *   targeting covers the device, so a checked device always means "THIS
 *   notification reaches THIS device".
 * - Checking a device adds it to THIS notification's targeting only (for the
 *   current browser it first runs the subscribe flow when needed); unchecking
 *   removes it from THIS notification's targeting only. Other notifications
 *   and the device's account-global subscription are NEVER touched from here
 *   — devices are managed in the admin device panel.
 * - One general guard: the select can never reach zero delivery — unchecking
 *   the last effective option (e-mail or final targeted device) is blocked
 *   with the min-one-channel toast.
 * - The address field ("naar ...") only shows while e-mail is selected.
 */
import React, {useContext, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {AwesomeIcon, getSetting, ResponseData, showSnackbar} from '@dvrd/dvr-controls';

import PortalSelectController from '../../controls/portalSelect/portalSelectController';

import NotificationInputController from './notificationInput/notificationInputController';

import {PortalSelectItem, PortalSelectOption, PortalSelectValue} from '../../../util/types';

import {DeviceTokenChannel, updateNotificationChannels} from '../../../util/requestSender';

import {
    computeSelectedChannelValues,
    currentDeviceFormFactor,
    deviceDisplayName,
    deviceOptionValue,
    deviceTypeIcon,
    EMAIL_CHANNEL_VALUE,
    ensurePushChannel,
    isCurrentDevice,
    isDeviceOptionValue,
    readNotificationChannels,
    readNotificationPushDeviceIds,
    THIS_DEVICE_VALUE,
    toggleEmailChannel,
    togglePushChannel,
    wouldLeaveNoDelivery
} from '../../../util/pushChannelUtil';

import {isMobileDevice} from '../../../util/pwaUtil';

import {getOptedIn, runPushHealthCheck} from '../../../hooks/usePushSubscription';

import {PushChannelsContext} from '../../pushNotifications/pushChannelsContext';

import {showPushPermissionGuide} from '../../pushNotifications/pushPermissionGuide';

// Aliased on purpose: importing the model as `Notification` would shadow the
// browser's global Notification API, silently breaking the permission checks
// below (`Notification.permission` would read the model class instead).
import NotificationModel from '../../../models/notificationModel';

import {AppContext} from '../../misc/appContext';

interface Props {
    notification: NotificationModel;
    disabled: boolean;
    onChangeEmailAddresses: (value: string) => void;
    onReplaceNotification: (notification: NotificationModel) => void;
}

export default function NotificationChannelsSelect(props: Props) {
    const {t} = useTranslation();
    const {notification, disabled, onChangeEmailAddresses, onReplaceNotification} = props;
    const appContext = useContext(AppContext);
    const pushContext = useContext(PushChannelsContext);
    const userId = appContext.userContext.user?.id ?? null;
    const accountId = appContext.accountContext.account?.id ?? null;
    const enableWebPush = getSetting('enableWebPush') === true;

    // Optimistic per-notification channel list; rolled back when the PATCH
    // fails. Resynced from the notification when the parent replaces the
    // instance (e.g. after a product-selection update returns a clone).
    const [channels, setChannels] = useState<Array<DeviceTokenChannel>>(() => readNotificationChannels(notification));
    // THIS notification's push-device targeting (flex.push_device_ids):
    // null = key absent = all devices (legacy). Optimistic like `channels`.
    const [deviceIds, setDeviceIds] = useState<Array<string> | null>(() => readNotificationPushDeviceIds(notification));
    // One channel action at a time — enable() can spend seconds in the native
    // permission prompt; meanwhile every option renders disabled and further
    // toggle events are ignored and resynced.
    const [busy, setBusy] = useState(false);
    // Keeps the synthetic "Dit apparaat" option visually CHECKED from the
    // moment the user checks it until the provider's device reload delivers
    // the real token row — without it the checkbox would snap back to
    // unchecked for the whole multi-second subscribe flow.
    const [pendingEnable, setPendingEnable] = useState(false);

    // Async flows (awaiting enable()/PATCHes) must read the LATEST channel
    // list, not the render-time closure — refs mirror the state.
    const channelsRef = useRef(channels);
    channelsRef.current = channels;
    const deviceIdsRef = useRef(deviceIds);
    deviceIdsRef.current = deviceIds;
    // Set when THIS card's enable() flow succeeds: the new token id is only
    // known once the provider refresh delivers the current-device row, so the
    // targeting PATCH is deferred to the effect watching currentDeviceRow.
    const pendingTargetAddRef = useRef(false);
    // The LATEST notification instance — the parent may replace it mid-flight
    // (e.g. a product-selection update returns a clone), so success callbacks
    // must mutate THIS one, never a stale render-time capture (republishing a
    // stale instance would resurrect old settings on the card).
    const notificationRef = useRef(notification);
    notificationRef.current = notification;
    // Late network callbacks must not update state (or re-toast) after the
    // card unmounted (notification deleted, page left).
    const mountedRef = useRef(true);
    useEffect(
        () => () => {
            mountedRef.current = false;
        },
        []
    );

    useEffect(() => {
        setChannels(readNotificationChannels(notification));
        setDeviceIds(readNotificationPushDeviceIds(notification));
    }, [notification]);

    const devices = pushContext?.devices ?? [];
    const currentEndpoint = pushContext?.currentEndpoint ?? null;
    const devicesLoading = pushContext?.devicesLoading ?? false;
    const pushAvailable = enableWebPush && !!pushContext;

    const errorToast = (message: string) =>
        showSnackbar(message, {backgroundColor: 'color-blue-bolmate', textColor: 'white'});

    // Any state change re-renders with a NEW selected-values array, which the
    // controlled PortalSelect syncs back into its internal state — this is how
    // an ignored/blocked toggle visually snaps back.
    const [, setSyncNonce] = useState(0);
    const resyncSelect = () => setSyncNonce(nonce => nonce + 1);

    // Backend = single source of truth: show ALL the account's active devices
    // (no per-browser opt-in filtering) so every browser renders the same set.
    // The local opt-in flag only gates the subscribe FLOW below, never which
    // devices a notification is shown to reach.
    const subscribedDevices = pushAvailable ? devices : [];
    const currentDeviceRow = pushAvailable
        ? devices.find(device => isCurrentDevice(device, currentEndpoint))
        : undefined;

    const baseSelectedValues = computeSelectedChannelValues({
        channels,
        devices: pushAvailable ? devices : [],
        deviceIds
    });
    const selectedValues =
        pendingEnable && !currentDeviceRow ? [...baseSelectedValues, THIS_DEVICE_VALUE] : baseSelectedValues;
    const hasEmail = channels.includes('EMAIL');

    // The subscribe flow finished end-to-end once the provider delivers this
    // browser's token row — the real row replaces the synthetic pending one.
    // When THIS card started that flow (pendingTargetAddRef), the new token id
    // is only known now: add it to THIS notification's targeting. WEB_PUSH
    // already on with null targeting (= all devices) needs no PATCH — the new
    // device is covered implicitly.
    useEffect(() => {
        if (!currentDeviceRow) return;
        setPendingEnable(false);
        if (!pendingTargetAddRef.current) return;
        pendingTargetAddRef.current = false;
        const newId = currentDeviceRow.id;
        const hasPush = channelsRef.current.includes('WEB_PUSH');
        const ids = deviceIdsRef.current;
        if (hasPush && ids === null) return;
        const nextIds = !hasPush || ids === null ? [newId] : ids.includes(newId) ? ids : [...ids, newId];
        void (async () => {
            setBusy(true);
            try {
                await patchChannels(ensurePushChannel(channelsRef.current), nextIds);
            } finally {
                if (mountedRef.current) {
                    setBusy(false);
                    resyncSelect();
                }
            }
        })();
    }, [currentDeviceRow?.id]);

    // Rebuilt every render on purpose: it derives from half the component's
    // state (busy, loading, devices, channels) and building ~10 small options
    // is cheaper than maintaining an exhaustive dependency list. The e-mail
    // channel and the push devices render as two visually separate sections
    // (a flat item + a labeled group) — they are independent toggles.
    function buildItems(): Array<PortalSelectItem<string>> {
        // The e-mail row shows the ACTUAL configured address(es) — "where will
        // this go" — not a generic "e-mail" label.
        const emailAddresses = notification.emailAddresses.filter(Boolean).join(', ');
        const emailItem: PortalSelectOption<string> = {
            value: EMAIL_CHANNEL_VALUE,
            disabled: busy,
            label: (
                <span className='portal-select-item-label channel-option'>
                    <AwesomeIcon name='envelope' className='channel-option-icon' />
                    <span className='channel-option-text' title={emailAddresses}>
                        {emailAddresses || t('notifications.components.channelEmail', 'e-mail')}
                    </span>
                </span>
            )
        };
        if (!pushAvailable) return [emailItem];
        const labels = {
            installedApp: t('push.installedApp', 'geïnstalleerde app'),
            unknownDevice: t('push.unknownDevice', 'Onbekend apparaat'),
            thisDevice: {
                mobile: t('push.thisPhone', 'Deze telefoon'),
                tablet: t('push.thisTablet', 'Deze tablet'),
                desktop: t('push.thisComputer', 'Deze computer')
            },
            formFactor: {
                mobile: t('push.formFactorPhone', 'Telefoon'),
                tablet: t('push.formFactorTablet', 'Tablet'),
                desktop: t('push.formFactorComputer', 'Computer')
            }
        };
        const deviceItems: Array<PortalSelectOption<string>> = [];
        // Current device first — it is the only actionable row.
        const orderedDevices = subscribedDevices
            .slice()
            .sort((a, b) => Number(isCurrentDevice(b, currentEndpoint)) - Number(isCurrentDevice(a, currentEndpoint)));
        let hasCurrentDeviceRow = false;
        for (const device of orderedDevices) {
            const isCurrent = isCurrentDevice(device, currentEndpoint);
            if (isCurrent) hasCurrentDeviceRow = true;
            deviceItems.push({
                // Every subscribed device is interactable: checking adds it to
                // THIS notification's targeting, unchecking removes it — other
                // notifications and the device's subscription stay untouched.
                value: deviceOptionValue(device.id),
                disabled: busy,
                label: (
                    <span className='portal-select-item-label channel-option'>
                        <AwesomeIcon name={deviceTypeIcon(device)} className='channel-option-icon' />
                        {deviceDisplayName(device, labels, isCurrent)}
                    </span>
                )
            });
        }
        if (!hasCurrentDeviceRow) {
            // This browser has no subscription yet: offer it as an unchecked
            // option. Checking it starts the subscribe flow. Outside a push
            // capable context (mobile browser tab, unsupported browser) the
            // option is visible but disabled, with the reason inline. Also
            // disabled while the device list is still loading — this browser
            // may turn out to be subscribed already, and a click during that
            // window would run a needless re-subscribe.
            const canEnableHere = !!pushContext && pushContext.state.isSupported && pushContext.state.canUseHere;
            deviceItems.push({
                value: THIS_DEVICE_VALUE,
                disabled: busy || devicesLoading || !canEnableHere,
                label: (
                    <span className='portal-select-item-label channel-option'>
                        <AwesomeIcon
                            name={isMobileDevice() ? 'mobile-screen-button' : 'desktop'}
                            className='channel-option-icon'
                        />
                        {canEnableHere
                            ? labels.thisDevice[currentDeviceFormFactor()]
                            : t('notifications.components.thisDeviceAppOnly', '{{device}} (alleen in de app)', {
                                  device: labels.thisDevice[currentDeviceFormFactor()]
                              })}
                    </span>
                )
            });
        }
        // Two labeled sections, like the sales-dashboard column selector:
        // the e-mail channel and the push devices.
        return [
            {groupLabel: t('notifications.components.channelsEmail', 'E-mail'), items: [emailItem]},
            {groupLabel: t('push.devices', 'Apparaten'), items: deviceItems}
        ];
    }

    function parseChannelsValue(value: PortalSelectValue<string>): string {
        const values = Array.isArray(value) ? value : [value];
        const emailSelected = values.includes(EMAIL_CHANNEL_VALUE);
        const deviceCount = values.filter(v => v !== EMAIL_CHANNEL_VALUE).length;
        const deviceText = `${deviceCount} ${
            deviceCount === 1
                ? t('notifications.components.deviceSingular', 'apparaat')
                : t('notifications.components.devicePlural', 'apparaten')
        }`;
        if (emailSelected && deviceCount)
            return `${t('notifications.components.channelEmail', 'e-mail')} + ${deviceText}`;
        if (emailSelected) return t('notifications.components.channelEmail', 'e-mail');
        if (deviceCount) return deviceText;
        return t('notifications.components.channelsNone', 'geen kanalen');
    }

    /** PATCH this notification's channels + push-device targeting,
     *  optimistically. `nextDeviceIds` semantics mirror the backend contract:
     *  null = OMIT the field (leave the stored targeting untouched — e.g. an
     *  e-mail-only toggle), [] = CLEAR it (the server strips WEB_PUSH and
     *  removes the key), non-empty = replace it. Resolves true on success;
     *  rolls back both states and toasts on failure. Callers own `busy`. */
    function patchChannels(next: Array<DeviceTokenChannel>, nextDeviceIds: Array<string> | null): Promise<boolean> {
        const previousChannels = channelsRef.current;
        const previousDeviceIds = deviceIdsRef.current;
        // Local mirror of the server normalization: a cleared list is stored
        // as "key absent" (null), never as [].
        const localDeviceIds = nextDeviceIds === null ? previousDeviceIds : nextDeviceIds.length ? nextDeviceIds : null;
        setChannels(next);
        setDeviceIds(localDeviceIds);
        return new Promise(resolve => {
            updateNotificationChannels({
                notification_id: notification.id,
                account_id: notification.accountId,
                channels: next,
                push_device_ids: nextDeviceIds === null ? undefined : nextDeviceIds,
                callback: (data: ResponseData) => {
                    if (!mountedRef.current) return resolve(!!data.success);
                    if (data.success) {
                        // Adopt the SERVER-normalized state from the response
                        // (ids may have been hygiene-filtered, channels
                        // normalized) instead of mirroring our own request —
                        // otherwise the UI can show push ON to a device the
                        // server just dropped from the stored targeting.
                        const serverNotification = (
                            data as {notification?: {channels?: Array<string> | null; flex?: unknown}}
                        ).notification;
                        const serverChannels = serverNotification ? readNotificationChannels(serverNotification) : next;
                        const serverDeviceIds = serverNotification
                            ? readNotificationPushDeviceIds(serverNotification)
                            : localDeviceIds;
                        setChannels(serverChannels);
                        setDeviceIds(serverDeviceIds);
                        // Mutate the LATEST instance (notificationRef), not the
                        // render-time capture, so a parent-side replacement is
                        // never overwritten by a stale object.
                        const target = notificationRef.current;
                        target.channels = serverChannels;
                        const flex = {...(target.flex ?? {})};
                        if (serverDeviceIds === null) delete flex.push_device_ids;
                        else flex.push_device_ids = serverDeviceIds;
                        target.flex = flex;
                        // Re-render the card so siblings reading
                        // notification.channels (the title's channels badge)
                        // pick the change up immediately.
                        onReplaceNotification(target);
                        resolve(true);
                    } else {
                        setChannels(previousChannels);
                        setDeviceIds(previousDeviceIds);
                        errorToast(t('push.error', 'Er ging iets mis, probeer het opnieuw'));
                        resolve(false);
                    }
                },
                errorCallback: () => {
                    if (mountedRef.current) {
                        setChannels(previousChannels);
                        setDeviceIds(previousDeviceIds);
                        errorToast(t('push.error', 'Er ging iets mis, probeer het opnieuw'));
                    }
                    resolve(false);
                }
            });
        });
    }

    /** Block-with-toast when a mutation would leave this notification with
     *  zero delivery (the single general guard — see wouldLeaveNoDelivery).
     *  Returns true when blocked, so callers can bail out before patching. */
    function blockIfNoDelivery(nextChannels: Array<DeviceTokenChannel>, nextDeviceIds: Array<string> | null): boolean {
        if (!wouldLeaveNoDelivery(nextChannels, nextDeviceIds, subscribedDevices)) return false;
        errorToast(t('notifications.components.minOneChannel', 'Kies minimaal één verzendkanaal'));
        resyncSelect();
        return true;
    }

    async function onToggleEmail(on: boolean) {
        const next = toggleEmailChannel(channelsRef.current, on);
        // E-mail may only go off when the notification STILL delivers
        // somewhere — push on with at least one effectively-targeted device.
        if (!on && blockIfNoDelivery(next, deviceIdsRef.current)) return;
        setBusy(true);
        try {
            // Targeting untouched: omit push_device_ids (null).
            await patchChannels(next, null);
        } finally {
            setBusy(false);
            resyncSelect();
        }
    }

    /** Checking a subscribed device (current OR another) adds it to THIS
     *  notification's targeting ONLY — no (re)subscription involved (these
     *  devices are already subscribed), no other notification touched. */
    async function onCheckSubscribedDevice(value: string) {
        const device = subscribedDevices.find(row => deviceOptionValue(row.id) === value);
        if (!device) {
            resyncSelect();
            return;
        }
        const hasPush = channelsRef.current.includes('WEB_PUSH');
        const ids = deviceIdsRef.current;
        // Push already on for ALL devices (legacy null targeting): the row
        // renders checked, so a "check" here is a UI race — nothing to do.
        if (hasPush && ids === null) {
            resyncSelect();
            return;
        }
        // Push off → start targeting with ONLY this device (never all);
        // push on with a list → append this device.
        const nextIds = !hasPush || ids === null ? [device.id] : ids.includes(device.id) ? ids : [...ids, device.id];
        setBusy(true);
        try {
            await patchChannels(ensurePushChannel(channelsRef.current), nextIds);
        } finally {
            setBusy(false);
            resyncSelect();
        }
    }

    async function onEnableThisDevice() {
        if (!pushContext) return;
        // Another card (or the same one) is already driving the subscribe
        // flow — the permission prompt must not be raced.
        if (pushContext.state.isSubscribing) {
            resyncSelect();
            return;
        }
        if (typeof Notification !== 'undefined' && Notification.permission === 'denied') {
            // Denied origins can't be re-prompted; show the settings guide —
            // with the push card admin-only this is the user's only recovery
            // path.
            showPushPermissionGuide(t, 'denied');
            resyncSelect();
            return;
        }
        setBusy(true);
        setPendingEnable(true);
        let subscribed = false;
        try {
            const enableError = await pushContext.enable();
            if (!mountedRef.current) return;
            if (enableError === 'device-limit') {
                errorToast(t('push.errors.deviceLimit', 'Maximaal aantal apparaten bereikt. Verwijder er eerst een.'));
                return;
            }
            if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
                errorToast(t('push.notGranted', 'Meldingen niet ingeschakeld: geen toestemming gegeven.'));
                return;
            }
            // enable() only sets the opt-in flag on FULL success (register may
            // still have failed after the permission grant).
            if (!getOptedIn(userId, accountId)) {
                errorToast(t('push.error', 'Er ging iets mis, probeer het opnieuw'));
                return;
            }
            subscribed = true;
            // Checking "Dit apparaat" means "push THIS notification to THIS
            // device" — but its token id is only known once the provider
            // refresh delivers the row. Flag it; the currentDeviceRow effect
            // runs the targeting PATCH with the real id (never a premature
            // all-devices WEB_PUSH toggle).
            pendingTargetAddRef.current = true;
            const vapid = getSetting('vapidPublicKey') as string;
            const health = await runPushHealthCheck(accountId, vapid);
            if (!mountedRef.current) return;
            if (!health.ok) {
                console.warn('[push] health check failed:', health.failed);
                errorToast(
                    t(
                        'push.healthWarn',
                        'Meldingen ingeschakeld, maar een controle faalde — ze komen mogelijk niet aan.'
                    )
                );
            }
        } finally {
            // On success pendingEnable stays on until the provider delivers
            // the real token row (see the effect above); on failure the
            // synthetic option must snap back to unchecked right away — and
            // the deferred targeting-add must not fire for a failed enable.
            if (!subscribed) {
                setPendingEnable(false);
                pendingTargetAddRef.current = false;
            }
            if (mountedRef.current) {
                setBusy(false);
                resyncSelect();
            }
        }
    }

    /** Unchecking a device removes it from THIS notification's targeting ONLY
     *  — no other notification changes and the device's account-global
     *  subscription is NEVER touched (it keeps receiving every other
     *  notification that targets it). Removing the last effective device
     *  drops WEB_PUSH and clears the targeting (send []) — unless that would
     *  leave the notification with zero delivery, which the guard blocks. */
    async function onUncheckDevice(value: string) {
        const device = subscribedDevices.find(row => deviceOptionValue(row.id) === value);
        if (!device) {
            resyncSelect();
            return;
        }
        const subscribedIds = subscribedDevices.map(row => row.id);
        // null targeting (= all devices) materializes to the full subscribed
        // set first; also drop ids of since-deleted devices (hygiene).
        const base = deviceIdsRef.current === null ? subscribedIds : deviceIdsRef.current;
        const nextIds = base.filter(id => id !== device.id && subscribedIds.includes(id));
        const nextChannels = nextIds.length
            ? ensurePushChannel(channelsRef.current)
            : channelsRef.current.filter(channel => channel !== 'WEB_PUSH');
        if (blockIfNoDelivery(nextChannels, nextIds)) return;
        setBusy(true);
        try {
            // nextIds === [] sends [] — the explicit "clear targeting" signal.
            await patchChannels(nextChannels, nextIds);
        } finally {
            if (mountedRef.current) {
                setBusy(false);
                resyncSelect();
            }
        }
    }

    /** The "Alles" / "Wis" header controls. Selecting all turns e-mail on and
     *  targets ALL currently subscribed devices explicitly; WITHOUT any
     *  subscribed device it runs the full subscribe flow for this device —
     *  including the native permission prompt — exactly like checking "Dit
     *  apparaat". Clearing falls back to e-mail only (never an empty
     *  selection) and clears the targeting; it never touches any device
     *  subscription. */
    async function onBulkToggle(on: boolean) {
        if (on && !subscribedDevices.length) {
            // "Alles" while this device is not subscribed yet: make sure
            // e-mail is on, then subscribe this device (fires the permission
            // prompt when it was never granted). Outside a push-capable
            // context (or while the device list still loads) only the e-mail
            // part applies.
            const canEnableHere =
                !!pushContext && pushContext.state.isSupported && pushContext.state.canUseHere && !devicesLoading;
            if (!channelsRef.current.includes('EMAIL')) {
                setBusy(true);
                try {
                    await patchChannels(toggleEmailChannel(channelsRef.current, true), null);
                } finally {
                    if (mountedRef.current) setBusy(false);
                }
            }
            if (canEnableHere && !currentDeviceRow) {
                await onEnableThisDevice();
                return;
            }
            resyncSelect();
            return;
        }
        const subscribedIds = subscribedDevices.map(row => row.id);
        let next = toggleEmailChannel(channelsRef.current, true);
        next = on ? ensurePushChannel(next) : togglePushChannel(next, false);
        const currentIds = deviceIdsRef.current;
        // No-op short-circuits — don't fire a PATCH that changes nothing.
        const channelsUnchanged =
            next.length === channelsRef.current.length && next.every(ch => channelsRef.current.includes(ch));
        const targetingUnchanged = on
            ? currentIds === null || subscribedIds.every(id => currentIds.includes(id))
            : currentIds === null;
        if (channelsUnchanged && targetingUnchanged) {
            resyncSelect();
            return;
        }
        setBusy(true);
        try {
            // "Alles" → explicit list of every subscribed device; "Wis" → []
            // clears the targeting alongside dropping WEB_PUSH.
            await patchChannels(next, on ? subscribedIds : []);
        } finally {
            if (mountedRef.current) {
                setBusy(false);
                resyncSelect();
            }
        }
    }

    function onChangeChannels(value: string | null, selected: boolean) {
        if (busy || disabled) {
            resyncSelect();
            return;
        }
        if (value === null) {
            // Bulk operation from the "Alles"/"Wis" header controls.
            void onBulkToggle(selected);
            return;
        }
        if (value === EMAIL_CHANNEL_VALUE) {
            void onToggleEmail(selected);
            return;
        }
        if (value === THIS_DEVICE_VALUE) {
            if (selected) void onEnableThisDevice();
            else resyncSelect();
            return;
        }
        if (isDeviceOptionValue(value)) {
            if (selected) void onCheckSubscribedDevice(value);
            else void onUncheckDevice(value);
            return;
        }
        resyncSelect();
    }

    return (
        <>
            <section>
                <label className='fixed-text'>
                    {t('notifications.components.sendNotificationVia', 'stuur een notificatie via')}
                </label>
                <PortalSelectController
                    value={selectedValues}
                    onChange={onChangeChannels}
                    items={buildItems()}
                    parseSelectValue={parseChannelsValue}
                    disabled={disabled || busy}
                    multiple
                    controls={['selectAll', 'deselectAll']}
                    size='medium'
                    outlined
                    itemsClassName='channels-select-items'
                />
            </section>
            {hasEmail && (
                <section>
                    <label className='fixed-text'>
                        {t('notifications.components.sendNotificationToShort', 'naar')}
                    </label>
                    <NotificationInputController
                        onSubmit={onChangeEmailAddresses}
                        value={notification.emailAddresses.join(',')}
                        label={t('notifications.components.sendEmailToLabel', 'Stuur e-mail naar')}
                        inputProps={{inputProps: {type: 'email', multiple: true}}}
                        disabled={disabled}
                    />
                </section>
            )}
        </>
    );
}
